import { useState } from 'react';
import { Button, Grid, TextField } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { ExecutionTypeDropdown } from '@/components/ExecutionTypeDropdown';
import { IntervalDialog } from '@/components/IntervalDialog';
import { ExerciseConfig, ExerciseConfigExecutionTypeEnum } from '@/generated';
import { numberValue } from '@/utils/number-utils';

interface ExerciseProps {
  exerciseConfig?: ExerciseConfig;
  updateExerciseConfig: (exerciseConfig: ExerciseConfig) => void;
}

export const ExerciseConfigComponent = (props: ExerciseProps) => {
  const [intervalDialogOpen, setIntervalDialogOpen] = useState(false);
  const exerciseConfig = props.exerciseConfig;

  if (!exerciseConfig) return null;

  const executionType = exerciseConfig.executionType ?? ExerciseConfigExecutionTypeEnum.Reps;
  const timing = exerciseConfig.timing ?? { intervals: [] };

  const updateExecutionType = (updatedConfig: ExerciseConfig) => {
    if (updatedConfig.executionType !== ExerciseConfigExecutionTypeEnum.Intervals) {
      props.updateExerciseConfig(updatedConfig);
      return;
    }

    const updatedTiming = updatedConfig.timing ?? { intervals: [] };
    props.updateExerciseConfig({
      ...updatedConfig,
      timing: {
        ...updatedTiming,
        initTimeSeconds: updatedTiming.initTimeSeconds ?? 5,
        switchPauseSeconds: updatedTiming.switchPauseSeconds ?? 5,
      },
    });
  };

  return (
    <Grid container spacing={2} sx={{ px: 1 }}>
      <Grid size={12}>
        <ExecutionTypeDropdown exerciseConfig={exerciseConfig} updateExerciseConfig={updateExecutionType} />
      </Grid>

      {executionType === ExerciseConfigExecutionTypeEnum.Reps && (
        <Grid size={12} sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
          <TextField
            label="Repetitions"
            type="number"
            fullWidth
            value={exerciseConfig.value ?? 0}
            onChange={(event) => props.updateExerciseConfig({ ...exerciseConfig, value: numberValue(event.target.value) })}
          />
          <TextField
            label="Weight"
            type="number"
            fullWidth
            value={exerciseConfig.weight ?? 0}
            onChange={(event) => props.updateExerciseConfig({ ...exerciseConfig, weight: numberValue(event.target.value) })}
          />
        </Grid>
      )}

      {executionType === ExerciseConfigExecutionTypeEnum.Duration && (
        <Grid size={12} sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
          <TextField
            label="Duration seconds"
            type="number"
            fullWidth
            value={exerciseConfig.value ?? 0}
            onChange={(event) => props.updateExerciseConfig({ ...exerciseConfig, value: numberValue(event.target.value) })}
          />
          <TextField
            label="Weight"
            type="number"
            fullWidth
            value={exerciseConfig.weight ?? 0}
            onChange={(event) => props.updateExerciseConfig({ ...exerciseConfig, weight: numberValue(event.target.value) })}
          />
        </Grid>
      )}

      {executionType === ExerciseConfigExecutionTypeEnum.Intervals && (
        <Grid size={12} sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
          <TextField
            label="Weight"
            type="number"
            fullWidth
            value={exerciseConfig.weight ?? 0}
            onChange={(event) => props.updateExerciseConfig({ ...exerciseConfig, weight: numberValue(event.target.value) })}
          />
          <Button variant="outlined" startIcon={<Edit />} onClick={() => setIntervalDialogOpen(true)}>
            Configure intervals
          </Button>
        </Grid>
      )}

      {intervalDialogOpen && (
        <IntervalDialog
          open
          intervals={timing.intervals.map((interval) => ({ ...interval }))}
          onCancel={() => setIntervalDialogOpen(false)}
          onSave={(intervals) => {
            props.updateExerciseConfig({ ...exerciseConfig, timing: { ...timing, intervals } });
            setIntervalDialogOpen(false);
          }}
        />
      )}
    </Grid>
  );
};
